/**
 * Shared help items for the application
 * Centralizes help documentation to ensure consistency across views
 */

// Common help items that are used in multiple places
export const commonHelpItems = {
  // Language indicators help
  languageIndicators: {
    title: 'Details',
    description: `Shows available languages for this video with color-coded status indicators:
      • Green (Published): Final version ready for distribution
      • Purple (Level 3): Reviewed by senior editor
      • Deep Purple (Level 2): Reviewed by editor
      • Blue (Level 1): Initial edit completed
      • Orange (AI Generated): Raw AI output, needs editing
      For a folder it shows the number of videos or folders available within it.`,
    position: 'bottom',
  },

  // Assignment chips help
  assignments: {
    title: 'Assignment Indicators',
    description: `(Only in the assignment toggle view) Video assignments show who is responsible for editing specific languages:
      • Solid chips: Assigned to you
      • Outlined chips: Assigned to others
      • Level indicator shows assignment category (Level 1-3)
      • Status indicators: ✓ (Completed), ⟳ (In Progress), ⚠️ (Overdue)
      Click on your assignments to update their status.`,
    position: 'bottom',
  },

  // File actions help
  fileActions: {
    title: 'Actions Menu',
    description: `Access video management options:
      • Rename: Change the video name or folder name
      • Move to Folder: Move the video or a folder to a different location       
      • Delete: Remove the video or a folder
      • Assign: Assign a language from a video to work on by team members, your assignments show up in the assignment view, with the count in the chip count
      • See Progress Stages: See the progress stages of the video (Between AI, Level 1 - 3, Published)
      • View Version History: See the edit versions history of languages associated with the video, You can restore any version from the version history. 
      • Regenerate: Regnerate the AI subtitles for the video. This will overwrite any existing AI subtitles.
      • Text 2 Text Translations: Translate the subtitles of the video from a source language to a target language(s).`,
    position: 'left',
  },
  
  // Header language chips help
  headerLanguageChips: {
    title: 'Language Access Chips',
    description: `Shows your access levels to different languages:
      • Green: ADMIN access - full control to edit and manage
      • Blue: WRITE access - can edit subtitles
      • Orange: READ access - can view but not edit
      Click any chip to see all your language permissions.`,
    position: 'bottom',
  },
  
  // Header menu help
  headerMenu: {
    title: 'User Menu',
    description: `Access account and system options:
      • Active Sessions: Manage open editing sessions
      • Help: Access application guidance
      • System Status: View system status
      • Video Processing Status: View video processing status
      • Report a bug: Report a bug, ensure to include the video ID and a description of the issue and the logs
      • Dark/Light Mode: Toggle display theme
      • Logout: End your current session`,
    position: 'bottom',
  },

  backtoDashboard: {
    title: 'Back to Dashboard',
    description: `Click here to return to the main dashboard view.`,
    position: 'left',
  }
};

// List view specific help items
export const userViewHelpItems = [
  {
    selector: '.header-language-chips',
    ...commonHelpItems.headerLanguageChips
  },
  {
    selector: '.header-user-menu',
    ...commonHelpItems.headerMenu
  },
  {
    selector: '.header-theme-toggle',
    title: 'Theme Toggle',
    description: 'Switch between light and dark display modes.',
    position: 'bottom',
  },
  {
    selector: '.breadcrumbs',
    title: 'Breadcrumbs',
    description: 'Shows the current folder path. Click to navigate to a parent folder.',
    position: 'bottom',
  },
  {
    selector: '.search-field',
    title: 'Search Field',
    description: 'Search videos by name, language, and process stages. You can search in the folder or across all folders.',
    position: 'bottom',
  },
  {
    selector: '.sort-selector',
    title: 'Sort Selector',
    description: 'Change how files are ordered (by name, date, etc.).',
    position: 'bottom',
  },
  {
    selector: '.view-toggle',
    title: 'Toggle between Grid and List View',
    description: 'Switch between Grid View and List View.',
    position: 'left',
  },
  {
    selector: '.view-assignement-button',
    title: 'View Assignments Button',
    description: 'Switch between All items and Assigned Items.',
    position: 'left',
  },
  {
    selector: '.refresh-button',
    title: 'Refresh Button',
    description: 'Refresh the current view to see the latest updates.',
    position: 'left',
  },
  {
    selector: '.create-folder-button',
    title: 'Create Folder Button',
    description: 'Create a new folder to organize your videos.',
    position: 'left',
  },
  {
    selector: '.upload-video-button',
    title: 'Upload Video Button',
    description: 'Upload a new video file to create subtitles. Supports MP4, MKV and other common video formats.',
    position: 'left',
  },
  {
    selector: '.file-folder-item',
    title: 'Video File or Folder',
    description: 'Click to open this video/file for editing or the navigate in the folder. The row shows key information about the video/folder file.',
    position: 'left',
  },
  {
    selector: '.file-folder-name',
    title: 'File Name',
    description: 'The name of the video file or the folder. For a video, below it you can see the video ID when available.',
    position: 'left',
  },
  {
    selector: '.folder-file-details',
    ...commonHelpItems.languageIndicators
  },
  
  {
    selector: '.file-folder-duration',
    title: 'Video Duration',
    description: 'The duration of the video file. This is often descriptive of the video content, or - for a folder',
    position: 'bottom',
  },
  {
    selector: '.file-folder-updated-by',
    title: 'Last Updated By',
    description: 'Shows who last modified this file.',
    position: 'bottom',
  },
  {
    selector: '.file-folder-updated-at',
    title: 'Last Updated At',
    description: 'Shows when this file was last modified.',
    position: 'bottom',
  },
  {
    selector: '.file-folder-action-menu-button',
    ...commonHelpItems.fileActions
  },
  
  {
    selector: '.list-file-assignments',
    ...commonHelpItems.assignments
  },
];

/**
 * Help items for administrative functions
 * These items provide guidance for user management, assignment management, and bug reports
 */
export const adminViewHelpItems = [
  // User Management
  {
    selector: '.user-management-section',
    title: 'User Management',
    description: `Manage all system users and their permissions:
      • View all registered users in the system
      • Create new user accounts with specific roles
      • Modify existing user permissions and language access
      • Reset passwords for users who need assistance
      • Deactivate accounts that are no longer needed`,
    position: 'right',
  },
  {
    selector: '.user-list',
    title: 'User List',
    description: `View and manage all system users:
      • Search for users by name or email
      • Click on any user to view their full profile
      • Sort users by name, role, or last activity date
      • Quick indicators show access levels and active status`,
    position: 'bottom',
  },
  {
    selector: '.user-create-button',
    title: 'Create New User',
    description: `Add a new user to the system:
      • Enter user's email address and basic information
      • Assign role (Admin, Editor, or Viewer)
      • Set language access permissions for each language
      • Set password or generate a random secure password
      • User will receive an email with login instructions`,
    position: 'left',
  },
  {
    selector: '.user-permissions-section',
    title: 'Language Permissions',
    description: `Manage language access for users:
      • READ: User can only view subtitles for this language
      • WRITE: User can edit subtitles for this language
      • ADMIN: User has full control over content in this language
      • Changes to permissions take effect immediately
      • Users can have different access levels for different languages`,
    position: 'bottom',
  },
  {
    selector: '.user-actions-menu',
    title: 'User Actions',
    description: `Perform administrative actions on user accounts:
      • Edit Profile: Update user information
      • Reset Password: Send password reset link
      • Modify Permissions: Change role or language access
      • Lock Account: Temporarily prevent login
      • Delete: Permanently remove user (use with caution)`,
    position: 'left',
  },

  // Assignment Management
  {
    selector: '.assignments-management-section',
    title: 'Assignment Management',
    description: `Oversee all subtitle editing assignments in the system:
      • View all active assignments across users and languages
      • Create new assignments for specific videos and languages
      • Track progress and completion status
      • Set and adjust deadlines
      • Reassign tasks when necessary`,
    position: 'right',
  },
  {
    selector: '.assignment-filters',
    title: 'Assignment Filters',
    description: `Filter the assignments view:
      • By User: See all tasks assigned to a specific user
      • By Language: View assignments for specific languages
      • By Status: Filter by Not Started, In Progress, or Completed
      • By Due Date: Find overdue assignments or upcoming deadlines
      • By Priority: Focus on high-priority tasks first`,
    position: 'bottom',
  },
  {
    selector: '.create-assignment-form',
    title: 'Create Assignment',
    description: `Assign subtitle editing work to users:
      • Select video from the repository
      • Choose target language for subtitling
      • Select appropriate user based on language skills
      • Set processing level (1-3) and instructions
      • Specify due date and priority
      • Add optional notes with specific guidance`,
    position: 'left',
  },
  {
    selector: '.batch-assignment-tool',
    title: 'Batch Assignment',
    description: `Create multiple assignments at once:
      • Upload CSV file with assignment details
      • Or select multiple videos and assign to same user
      • Set common parameters for all assignments
      • Review before confirming
      • Users will be notified of new assignments automatically`,
    position: 'bottom',
  },
  {
    selector: '.assignment-progress-chart',
    title: 'Progress Overview',
    description: `Monitor assignment completion status:
      • Visual representation of assignment progress
      • Track completion percentages by language
      • Identify bottlenecks in the workflow
      • See upcoming deadlines and at-risk assignments
      • Export reports for offline analysis`,
    position: 'left',
  },

  // Bug Reports
  {
    selector: '.bug-reports-section',
    title: 'Bug Reports',
    description: `Review and manage reported issues:
      • View all user-reported problems
      • Sort by date, status, or severity
      • Assign bugs to team members for resolution
      • Track resolution progress
      • Communicate updates to users who reported issues`,
    position: 'right',
  },
  {
    selector: '.bug-list',
    title: 'Bug List',
    description: `List of all reported bugs in the system:
      • Bug ID and description
      • Reported by and submission date
      • Current status (New, In Progress, Resolved)
      • Assigned to (team member responsible)
      • Priority level (Low, Medium, High, Critical)
      • Click on any row for full details including screenshots`,
    position: 'bottom',
  },
  {
    selector: '.bug-detail-panel',
    title: 'Bug Details',
    description: `Detailed information about the selected bug:
      • Full description and steps to reproduce
      • Technical information (browser, OS, page URL)
      • Video ID and timestamp (if applicable)
      • User-provided screenshots
      • System logs from time of occurrence
      • Communication history about this issue`,
    position: 'left',
  },
  {
    selector: '.bug-status-selector',
    title: 'Update Bug Status',
    description: `Change the status of a reported bug:
      • New: Bug has been reported but not yet addressed
      • In Progress: Team is working on a resolution
      • Need Info: More information required from reporter
      • Resolved: Bug has been fixed
      • Closed: Issue has been resolved or determined not to be a bug`,
    position: 'bottom',
  },
  {
    selector: '.bug-response-form',
    title: 'Respond to Bug Report',
    description: `Communicate with the user who reported the bug:
      • Send an update on resolution progress
      • Request additional information if needed
      • Notify when the bug has been fixed
      • Suggest workarounds when available
      • Thank users for their contribution to improving the system`,
    position: 'bottom',
  },

  // System Status Dashboard
  {
    selector: '.system-status-dashboard',
    title: 'System Status',
    description: `Monitor system health and performance:
      • Server status and uptime
      • Processing queue length
      • Storage usage and limits
      • Recent errors and warnings
      • API response times
      • Video processing statistics`,
    position: 'right',
  },
  {
    selector: '.video-processing-queue',
    title: 'Video Processing Queue',
    description: `Manage videos currently being processed:
      • View all videos in the transcription/translation queue
      • Check processing status and estimated completion time
      • Prioritize specific videos in the queue
      • Pause or restart processing tasks
      • Troubleshoot failed processing jobs`,
    position: 'bottom',
  },
  {
    selector: '.system-settings-panel',
    title: 'System Settings',
    description: `Configure application-wide settings:
      • API connection parameters
      • AI transcription quality presets
      • Default video processing options
      • Email notification preferences
      • System maintenance windows
      • Log level and retention policies`,
    position: 'left',
  }
];

/**
 * User Management page help items
 */
export const userManagementHelpItems = [
  {
    selector: '.user-management-header',
    title: 'User Management',
    description: `Manage all system users and their permissions from this page:
      • View all registered users in the system
      • Create new user accounts with specific roles
      • Modify existing user permissions and language access
      • Reset passwords for users who need assistance
      • Bulk Import users from CSV file`,
    position: 'bottom',
  },
  {
    selector: '.user-list',
    title: 'User List',
    description: `View, Filter and manage all system users:
      • Search for users by name or email
      • Click on any user to view their full profile
      • Sort users by name, role, or last activity date
      • Quick indicators show access levels and active status
      • Filter By Role: Show only Admins, Editors, or Viewers
      • Filter By Status: Active or Inactive accounts
      • Filter By Language Access: Filter by language permissions
      • Filter By Last Activity: Recently active or inactive users`,
    position: 'left',
  },
  {
    selector: '.user-create-button',
    title: 'Create New User',
    description: `Add a new user to the system:
      • Enter user's email address and basic information
      • Assign role (Admin, Editor, or Viewer)
      • Set language access permissions for each language
      • Set password or generate a random secure password
      • User will receive an email with login instructions`,
    position: 'left',
  },
];

/**
 * User Edit page help items - TODO - for user edit dialog connect this
 */
export const userEditHelpItems = [
  {
    selector: '.user-profile-section',
    title: 'User Profile',
    description: `Edit basic user information:
      • Name and contact information
      • Profile picture
      • Account status (Active/Inactive)
      • Notes and administrative comments`,
    position: 'right',
  },
  {
    selector: '.user-permissions-section',
    title: 'Language Permissions',
    description: `Manage language access for this user:
      • READ: User can only view subtitles for this language
      • WRITE: User can edit subtitles for this language
      • ADMIN: User has full control over content in this language
      • Changes to permissions take effect immediately
      • Users can have different access levels for different languages`,
    position: 'bottom',
  },
  {
    selector: '.user-role-selector',
    title: 'User Role',
    description: `Set the user's system-wide role:
      • Admin: Full system access including user management
      • Editor: Can manage content but not system settings
      • Viewer: Read-only access to content
      • Role changes affect available features and permissions`,
    position: 'right',
  },
  {
    selector: '.user-password-reset',
    title: 'Reset Password',
    description: `Manage this user's password:
      • Send password reset email to the user
      • Manually set a new password
      • Generate a random secure password
      • Force password change on next login`,
    position: 'bottom',
  },
  {
    selector: '.user-activity-log',
    title: 'Activity History',
    description: `View this user's system activity:
      • Recent logins and session information
      • Content edits and changes made
      • Videos accessed and edited
      • Administrative actions performed (if applicable)`,
    position: 'left',
  }
];

/**
 * Assignment Management page help items - TODO - need to add create functionality 
 */
export const assignmentManagementHelpItems = [
  {
    selector: '.assignments-management-header',
    title: 'Assignment Management',
    description: `Oversee all subtitle editing assignments in the system:
      • View all active assignments across users and languages
      • Create new assignments for specific videos and languages
      • Track progress and completion status
      • Set and adjust deadlines as needed`,
    position: 'bottom',
  },
  {
    selector: '.assignment-filters',
    title: 'Assignment Filters',
    description: `Filter the assignments view:
      • By User: See all tasks assigned to a specific user
      • By Language: View assignments for specific languages
      • By Status: Filter by Not Started, In Progress, or Completed
      • By Due Date: Find overdue assignments or upcoming deadlines
      • By Priority: Focus on high-priority tasks first`,
    position: 'bottom',
  },
  {
    selector: '.create-assignment-button', // TODO - NEED TO support this functionality 
    title: 'Create Assignment',
    description: `Create a new subtitle editing assignment:
      • Select video and target language
      • Choose assignee based on language skills
      • Set processing level and due date
      • Add specific instructions for the editor`,
    position: 'left',
  },
  {
    selector: '.assignment-list',
    title: 'Assignment List',
    description: `View all current assignments:
      • Sortable list of all assigned tasks
      • Color-coded by status and priority
      • Click any row to view or edit assignment details
      • Indicators for overdue or at-risk assignments`,
    position: 'bottom',
  }
];

/**
 * Create/Edit Assignment page help items - This is for the assign dialog - TODO conneect this 
 */
export const assignmentEditHelpItems = [
  {
    selector: '.assignment-details-form',
    title: 'Assignment Details',
    description: `Configure the subtitle editing assignment:
      • Video Selection: Choose which video needs subtitling
      • Language: Select target language for subtitles
      • Processing Level: Set editing level (1-3) required`,
    position: 'right',
  },
  {
    selector: '.assignment-user-selector',
    title: 'Assign To User',
    description: `Choose which user should complete this task:
      • Only users with appropriate language permissions are shown
      • System shows user workload indicator to help balance tasks
      • You can see each user's current assignment count
      • Filters help find users with specific skills`,
    position: 'bottom',
  },
  {
    selector: '.assignment-schedule-section',
    title: 'Level & Priority',
    description: `Set timing and importance for this assignment:
      • Due Date: When the task should be completed
      • Level: High/Medium/Low importance`,
    position: 'left',
  },
  {
    selector: '.assignment-instructions',
    title: 'Special Instructions',
    description: `Provide guidance for completing this assignment:
      • Add notes about special terminology
      • Mention style preferences or requirements
      • Include links to reference materials
      • Specify quality expectations`,
    position: 'bottom',
  }
];

/**
 * Bug Reports page help items - TODO Check if needed
 */
export const bugReportManagementHelpItems = [
  {
    selector: '.bug-reports-header',
    title: 'Bug Reports',
    description: `Review and manage reported issues:
      • View all user-reported problems
      • Sort by date, status, or severity
      • Assign bugs to team members for resolution
      • Track resolution progress`,
    position: 'bottom',
  },
  {
    selector: '.bug-list',
    title: 'Bug List',
    description: `List of all reported bugs in the system:
      • Bug ID and description
      • Reported by and submission date
      • Current status (New, In Progress, Resolved)
      • Assigned to (team member responsible)
      • Priority level (Low, Medium, High, Critical)`,
    position: 'bottom',
  },
  {
    selector: '.bug-filters',
    title: 'Filter Bug Reports',
    description: `Narrow down the bug list:
      • By Status: Focus on New, In Progress, or Resolved bugs
      • By Priority: Address Critical or High issues first
      • By Reporter: View bugs from specific users
      • By Date Range: Recent reports or historical issues`,
    position: 'right',
  },
  {
    selector: '.assign-bug-button',
    title: 'Assign Bug',
    description: `Delegate bug resolution responsibility:
      • Choose team member with appropriate skills
      • Set priority and target resolution date
      • Add notes with troubleshooting suggestions
      • Notify assignee automatically by email`,
    position: 'left',
  }
];

/**
 * Bug Detail page help items - - TODO Check if needed 
 */
export const bugDetailHelpItems = [
  {
    selector: '.bug-detail-panel',
    title: 'Bug Details',
    description: `Detailed information about the selected bug:
      • Full description and steps to reproduce
      • Technical information (browser, OS, page URL)
      • Video ID and timestamp (if applicable)
      • User-provided screenshots
      • System logs from time of occurrence`,
    position: 'right',
  },
  {
    selector: '.bug-status-selector',
    title: 'Update Bug Status',
    description: `Change the status of this reported bug:
      • New: Bug has been reported but not yet addressed
      • In Progress: Team is working on a resolution
      • Need Info: More information required from reporter
      • Resolved: Bug has been fixed
      • Closed: Issue has been resolved or determined not to be a bug`,
    position: 'bottom',
  },
  {
    selector: '.bug-response-form',
    title: 'Respond to Bug Report',
    description: `Communicate with the user who reported the bug:
      • Send an update on resolution progress
      • Request additional information if needed
      • Notify when the bug has been fixed
      • Suggest workarounds when available`,
    position: 'bottom',
  },
  {
    selector: '.bug-history-log',
    title: 'Resolution History',
    description: `Track the bug resolution process:
      • Timeline of status changes and updates
      • Communication history with the reporter
      • Notes from team members working on the issue
      • Related system changes or fixes applied`,
    position: 'left',
  }
];

/**
 * System Status page help items- TODO Check if needed
 */
export const systemStatusHelpItems = [
  {
    selector: '.system-status-dashboard',
    title: 'System Status Overview',
    description: `Monitor overall system health:
      • Server status and uptime
      • Processing queue length
      • Storage usage and limits
      • Recent errors and warnings
      • API response times`,
    position: 'bottom',
  },
  {
    selector: '.video-processing-queue',
    title: 'Video Processing Queue',
    description: `Manage videos currently being processed:
      • View all videos in the transcription/translation queue
      • Check processing status and estimated completion time
      • Prioritize specific videos in the queue
      • Pause or restart processing tasks`,
    position: 'right',
  },
  {
    selector: '.system-metrics-graphs',
    title: 'Performance Metrics',
    description: `View system performance over time:
      • CPU and memory usage trends
      • API response time history
      • Storage space utilization
      • User activity levels
      • Video processing throughput`,
    position: 'bottom',
  },
  {
    selector: '.system-alerts',
    title: 'System Alerts',
    description: `Critical notifications about system issues:
      • Error rate warnings
      • Resource limitation alerts
      • Security notifications
      • Maintenance reminders
      • Click any alert for detailed information`,
    position: 'left',
  }
];

/**
 * System Settings page help items - TODO Check if needed
 */
export const systemSettingsHelpItems = [
  {
    selector: '.system-settings-panel',
    title: 'System Settings',
    description: `Configure application-wide settings:
      • API connection parameters
      • AI transcription quality presets
      • Default video processing options
      • Email notification preferences
      • System maintenance windows`,
    position: 'bottom',
  },
  {
    selector: '.transcription-settings',
    title: 'Transcription Settings',
    description: `Configure AI transcription behavior:
      • Default language detection
      • Speech recognition quality vs. speed balance
      • Speaker diarization settings
      • Minimum confidence thresholds
      • Special terminology dictionaries`,
    position: 'right',
  },
  {
    selector: '.notification-settings',
    title: 'Notification Settings',
    description: `Manage system-wide notifications:
      • Email templates for different notification types
      • Default notification frequency
      • User notification preferences
      • Urgent alert thresholds
      • Notification delivery methods`,
    position: 'left',
  },
  {
    selector: '.backup-restore-section',
    title: 'Backup & Restore',
    description: `Manage system data protection:
      • Configure automatic backup schedule
      • Perform manual backups
      • Access backup history
      • Restore data from previous backups
      • Download backups for offline storage`,
    position: 'bottom',
  }
];

/**
 * Active Sessions page help items - TODO Check if needed
 */
export const activeSessionsHelpItems = [
  {
    selector: '.session-tabs',
    title: 'Session Types',
    description: `Switch between different session views:
      • My Sessions: View only your active editing sessions
      • All Users' Sessions: (Admin only) View active sessions across all users
      Click the appropriate tab to switch views.`,
    position: 'bottom',
  },
  {
    selector: '.sessions-list',
    title: 'Active Sessions',
    description: `View all currently active subtitle editing sessions:
      • See which videos and languages are being edited
      • Check when sessions were started and last active
      • Monitor total session duration
      • End sessions that are no longer needed`,
    position: 'bottom',
  },
  {
    selector: '.refresh-button',
    title: 'Refresh Sessions',
    description: 'Update the active sessions list to see the latest status.',
    position: 'left',
  },
];

/**
 * Video processing status help items
 */
export const videoProcessingHelpItems = [
  {
    selector: '.processing-filters',
    title: 'Filter Options',
    description: `Refine the videos displayed:
      • Time Range: Last 24 hours, 7 days, or 30 days
      • Status: All, In Progress, Completed, or Failed
      • Search: Filter by video name or ID`,
    position: 'bottom',
  },
  {
    selector: '.processing-status-summary',
    title: 'Processing Summary',
    description: `Overview of video processing metrics:
      • Current queue size and completion rate
      • Average processing times for different stages
      • Success and failure statistics
      These stats help monitor system performance.`,
    position: 'bottom',
  },
  {
    selector: '.video-processing-queue',
    title: 'Processing Queue',
    description: `List of videos being processed:
      • View processing status and progress for each video
      • See detailed timeline of processing steps
      • Identify bottlenecks or failures in the pipeline
      • View estimated completion times`,
    position: 'bottom',
  },
];

/**
 * MongoDB Files Browser help items
 */
export const mongoFilesBrowserHelpItems = [
  {
    selector: '.breadcrumbs-navigation',
    title: 'Folder Navigation',
    description: `Navigate through the MongoDB file structure:
      • Click folder names to move inside folders
      • Use breadcrumbs to quickly navigate up the hierarchy
      • View current path at the top of the page`,
    position: 'bottom',
  },
  {
    selector: '.files-list',
    title: 'Files List',
    description: `View files stored in the database:
      • See file metadata including IDs and creation dates
      • Identify file types and sizes
      • View hierarchical folder structure
      • Manage file organization`,
    position: 'top',
  },

  {
    selector: '.back-to-dashboard',
    ...commonHelpItems.backtoDashboard
  }
  
];

/**
 * Data Migration help items
 */
export const dataMigrationHelpItems = [
  {
    selector: '.user-data-migration',
    title: 'User Data Migration',
    description: `Transfer user accounts and permissions:
      • Migrate user profiles and access rights
      • Preserve language access settings
      • Transfer assignment information
      • Keep user activity history`,
    position: 'bottom',
  },
  {
    selector: '.file-system-migration',
    title: 'File System Migration',
    description: `Transfer files between storage systems:
      • Migrate from SQL to MongoDB storage
      • Monitor migration progress and status
      • View file counts and transfer statistics
      • Rollback options in case of issues`,
    position: 'bottom',
  },
  {
    selector: '.video-data-migration',
    title: 'Video Data Migration',
    description: `Transfer video content between systems:
      • Migrate video metadata and subtitles
      • Preserve all language translations
      • Maintain version history during migration
      • Transfer processing status information`,
    position: 'bottom',
  },
  
  {
    selector: '.migration-status',
    title: 'Migration Status',
    description: `Monitor ongoing migrations:
      • View real-time progress indicators
      • See completed vs. remaining items
      • Check for any errors or warnings
      • Review migration history`,
    position: 'top',
  },
];

/**
 * Admin dashboard help items
 */
export const adminDashboardHelpItems = [
  {
    selector: '.team-management-section',
    title: 'Team Management',
    description: `Tools for managing your team and organization:
      • User Management: Create and modify user accounts
      • Assignment Management: Track work allocated to team members
      • Bug Reports: View and address reported issues`,
    position: 'bottom',
  },
  {
    selector: '.admin-tools-section',
    title: 'Administrative Tools',
    description: `System-level administrative features:
      • Files & Folders: Manage content organization
      • Videos: Manage video assets directly
      • Data Migration: Transfer data between systems`,
    position: 'bottom',
  },
  {
    selector: '.user-management-card',
    title: 'User Management',
    description: `Create and manage user accounts:
      • Add new team members with specific roles
      • Set language access permissions
      • Reset passwords and manage account status`,
    position: 'right',
  },
  {
    selector: '.assignment-management-card',
    title: 'Assignment Management',
    description: `Track and manage work assignments:
      • Assign subtitle editing tasks to team members
      • Set deadlines and monitor progress
      • View completed and outstanding work`,
    position: 'right',
  },
  {
    selector: '.bug-reports-card',
    title: 'Bug Reports',
    description: `Handle reported application issues:
      • Review bug reports submitted by users
      • Track resolution status and progress
      • Communicate with users about fixes`,
    position: 'right',
  },
];

/**
 * Trash Bin help items
 */
export const trashBinHelpItems = [
  {
    selector: '.trash-bin-header',
    title: 'Recently Deleted Items',
    description: `View items that have been deleted within the last 30 days:
      • Items are automatically removed after 30 days
      • Restore items to their original or a new location
      • Permanently delete items before the 30-day period`,
    position: 'bottom',
  },
  {
    selector: '.trash-table',
    title: 'Deleted Items List',
    description: `Manage your deleted items:
      • See when and by whom items were deleted
      • View the original location of each item
      • Restore folders to their original or a new location
      • Permanently remove items with the delete action`,
    position: 'top',
  },
  {
    selector: '.refresh-button',
    title: 'Refresh',
    description: 'Update the list to see the latest deleted items.',
    position: 'left',
  },
];

